<script lang="ts" setup>
import { usePartnerBranding } from "~/composables/usePartnerBranding"

/**
 * Co-branded band shown *above* the normal Dapamas footer in partner mode.
 *
 * Unlike the former PartnerFooter it does NOT replace the Dapamas footer — it
 * sits on top of it as a thin pre-footer, mirroring `PartnerTopBar` at the
 * bottom of the page. It carries the "shopping through <partner>" trust message,
 * the partner's contact/support as a compact inline row (intentionally not the
 * column layout of a full footer) and the legal imprint (partner = legal entity,
 * Dapamas = technical provider). Colors follow the `--partner-footer-*` tokens.
 */
const { activePartner } = usePartnerBranding()
</script>

<template>
    <div
        v-if="activePartner"
        class="partner-prefooter"
        data-cy="partner-pre-footer"
    >
        <div
            class="max-container tw-px-4 tw-py-4 tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-4"
        >
            <!-- Brand + trust message -->
            <div class="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
                <span
                    class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium"
                >
                    <q-icon
                        name="fa-solid fa-lock"
                        size="12px"
                        class="tw-shrink-0 tw-opacity-70"
                    />
                    {{
                        $t("partner.shopping_through", {
                            name: activePartner.name,
                        })
                    }}
                </span>
            </div>

            <!-- Contact / support: compact inline row, not footer columns -->
            <div
                class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2"
            >
                <a
                    v-if="activePartner.contact.email"
                    :href="`mailto:${activePartner.contact.email}`"
                    class="partner-prefooter__item"
                >
                    <q-icon name="fa-solid fa-envelope" size="14px" />
                    <span class="tw-break-all">{{
                        activePartner.contact.email
                    }}</span>
                </a>
                <a
                    v-if="activePartner.contact.phone"
                    :href="`tel:${activePartner.contact.phone}`"
                    class="partner-prefooter__item"
                >
                    <q-icon name="fa-solid fa-phone" size="14px" />
                    <span>{{ activePartner.contact.phone }}</span>
                </a>
                <a
                    v-if="activePartner.contact.supportUrl"
                    :href="activePartner.contact.supportUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="partner-prefooter__item"
                >
                    <q-icon name="fa-solid fa-circle-question" size="14px" />
                    <span>{{
                        $t("partner.support", { name: activePartner.name })
                    }}</span>
                </a>
            </div>
        </div>

        <!-- Legal imprint -->
        <div class="partner-prefooter__divider"></div>
        <div class="max-container tw-px-4 tw-py-2.5">
            <p
                class="partner-prefooter__muted tw-text-xs tw-leading-relaxed tw-text-center tw-m-0"
            >
                {{ activePartner.legalEntity }}
            </p>
        </div>
    </div>
</template>

<style scoped>
.partner-prefooter {
    background: var(--partner-footer-bg, #004482);
    color: var(--partner-footer-text, #fff);
}

.partner-prefooter__item {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--partner-footer-link, #fff);
    transition: opacity 0.2s ease;
}

.partner-prefooter__item:hover {
    opacity: 0.7;
    text-decoration: underline;
}

.partner-prefooter__muted {
    color: var(--partner-footer-muted, rgba(255, 255, 255, 0.78));
}

.partner-prefooter__divider {
    height: 1px;
    background: color-mix(
        in srgb,
        var(--partner-footer-text, #fff) 18%,
        transparent
    );
}
</style>
